import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, Plus, ChevronDown, ChevronRight, TrendingUp, TrendingDown,
  DollarSign, Calendar, Receipt, X, Upload, Search, Scan, Link2, BookOpen,
  Home, Shield, FileText, Car, Wrench, Monitor, Briefcase, GraduationCap,
  Megaphone, Users, Utensils, Plane, Landmark, Package,
  Download, AlertTriangle, Bell, BellOff, Settings, RefreshCw, Check, Sparkles,
  ArrowRight, ExternalLink, CheckCircle2, Clock, Bot, Send, Loader2,
  CreditCard, Banknote, Eye, Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import MileageTab from './company-operations/MileageTab';

// ═══════════════════════════════════════════════════════════════════════════
// EXPENSE CATEGORIES - 16 Categories, 63 Subcategories
// ═══════════════════════════════════════════════════════════════════════════

const EXPENSE_CATEGORIES = [
  {
    id: 'fixed_overhead',
    name: 'Fixed Overhead',
    icon: Home,
    color: 'blue',
    subcategories: [
      { id: 'mortgage_rent', name: 'Mortgage / Rent' },
      { id: 'electricity', name: 'Electricity' },
      { id: 'water_sewer', name: 'Water & Sewer' },
      { id: 'phone_service', name: 'Phone Service' },
      { id: 'internet', name: 'Internet' },
      { id: 'disposal_waste', name: 'Disposal & Waste' },
    ]
  },
  {
    id: 'insurance',
    name: 'Insurance',
    icon: Shield,
    color: 'purple',
    subcategories: [
      { id: 'general_liability', name: 'General Liability' },
      { id: 'workers_comp', name: 'Workers Comp' },
      { id: 'business_insurance', name: 'Business Insurance' },
      { id: 'equipment_insurance', name: 'Equipment Insurance' },
    ]
  },
  {
    id: 'licenses_permits',
    name: 'Licenses & Permits',
    icon: FileText,
    color: 'cyan',
    subcategories: [
      { id: 'business_license', name: 'Business License' },
      { id: 'contractor_license', name: 'Contractor License' },
      { id: 'trade_licenses', name: 'Trade Licenses' },
    ]
  },
  {
    id: 'vehicle',
    name: 'Vehicle Expenses',
    icon: Car,
    color: 'amber',
    subcategories: [
      { id: 'gas_fuel', name: 'Gas & Fuel' },
      { id: 'vehicle_repairs', name: 'Vehicle Repairs & Maintenance' },
      { id: 'vehicle_insurance', name: 'Vehicle Insurance' },
      { id: 'vehicle_registration', name: 'Vehicle Registration' },
      { id: 'vehicle_wash', name: 'Vehicle Wash & Road Services' },
      { id: 'vehicle_rental', name: 'Vehicle Rental' },
    ]
  },
  {
    id: 'tools_equipment',
    name: 'Tools & Equipment',
    icon: Wrench,
    color: 'emerald',
    subcategories: [
      { id: 'tool_purchases', name: 'Tool Purchases' },
      { id: 'equipment_rental', name: 'Equipment Rental' },
      { id: 'equipment_repairs', name: 'Repairs & Maintenance' },
      { id: 'small_tools', name: 'Small Tools & Equipment' },
    ]
  },
  {
    id: 'office',
    name: 'Office Expenses',
    icon: Building2,
    color: 'slate',
    subcategories: [
      { id: 'office_supplies', name: 'Office Supplies' },
      { id: 'printing_copying', name: 'Printing & Photocopying' },
      { id: 'shipping_postage', name: 'Shipping & Postage' },
    ]
  },
  {
    id: 'software_tech',
    name: 'Software & Technology',
    icon: Monitor,
    color: 'indigo',
    subcategories: [
      { id: 'software_apps', name: 'Software & Apps' },
      { id: 'subscriptions', name: 'Memberships & Subscriptions' },
      { id: 'it_services', name: 'IT Services' },
    ]
  },
  {
    id: 'professional_services',
    name: 'Legal & Accounting Services',
    icon: Briefcase,
    color: 'rose',
    subcategories: [
      { id: 'accounting_fees', name: 'Accounting Fees' },
      { id: 'legal_fees', name: 'Legal Fees' },
    ]
  },
  {
    id: 'education',
    name: 'Education & Training',
    icon: GraduationCap,
    color: 'teal',
    subcategories: [
      { id: 'continuing_education', name: 'Continuing Education' },
      { id: 'certifications', name: 'Certifications' },
      { id: 'training_materials', name: 'Training Materials' },
      { id: 'uniforms', name: 'Uniforms' },
    ]
  },
  {
    id: 'marketing',
    name: 'Advertising & Marketing',
    icon: Megaphone,
    color: 'pink',
    subcategories: [
      { id: 'online_advertising', name: 'Online Advertising' },
      { id: 'print_advertising', name: 'Print Advertising' },
      { id: 'website', name: 'Website' },
      { id: 'promotional_items', name: 'Promotional Items' },
    ]
  },
  {
    id: 'memberships',
    name: 'Memberships',
    icon: Users,
    color: 'orange',
    subcategories: [
      { id: 'trade_associations', name: 'Trade Associations' },
      { id: 'chamber_commerce', name: 'Chamber of Commerce' },
      { id: 'other_memberships', name: 'Other Memberships' },
    ]
  },
  {
    id: 'payroll',
    name: 'Payroll Expenses',
    icon: DollarSign,
    color: 'green',
    subcategories: [
      { id: 'owner_draw', name: 'Owner Draw' },
      { id: 'employee_wages', name: 'Employee Wages' },
      { id: 'contract_labor', name: 'Contract Labor' },
      { id: 'payroll_taxes', name: 'Payroll Taxes' },
      { id: 'fica_medicare', name: 'FICA / Medicare' },
      { id: 'futa_suta', name: 'FUTA / SUTA' },
      { id: 'state_taxes', name: 'State Taxes' },
    ]
  },
  {
    id: 'meals_entertainment',
    name: 'Meals & Entertainment',
    icon: Utensils,
    color: 'red',
    subcategories: [
      { id: 'meals', name: 'Meals' },
      { id: 'entertainment', name: 'Entertainment' },
    ]
  },
  {
    id: 'travel',
    name: 'Travel',
    icon: Plane,
    color: 'sky',
    subcategories: [
      { id: 'airfare', name: 'Airfare' },
      { id: 'hotels', name: 'Hotels' },
      { id: 'mileage_reimbursement', name: 'Mileage Reimbursement' },
    ]
  },
  {
    id: 'banking',
    name: 'Bank Fees & Charges',
    icon: Landmark,
    color: 'gray',
    subcategories: [
      { id: 'bank_fees', name: 'Bank Fees & Service Charges' },
      { id: 'credit_card_fees', name: 'Credit Card Processing' },
      { id: 'interest_charges', name: 'Interest Charges' },
    ]
  },
  {
    id: 'other',
    name: 'Other Expenses',
    icon: Package,
    color: 'neutral',
    subcategories: [
      { id: 'donations', name: 'Donations & Contributions' },
      { id: 'miscellaneous', name: 'Miscellaneous' },
    ]
  }
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

const getCategoryColor = (color) => {
  const colors = {
    blue: { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6' },
    purple: { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.3)', text: '#8b5cf6' },
    cyan: { bg: 'rgba(6, 182, 212, 0.1)', border: 'rgba(6, 182, 212, 0.3)', text: '#06b6d4' },
    amber: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', text: '#f59e0b' },
    emerald: { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', text: '#10b981' },
    slate: { bg: 'rgba(100, 116, 139, 0.1)', border: 'rgba(100, 116, 139, 0.3)', text: '#64748b' },
    indigo: { bg: 'rgba(99, 102, 241, 0.1)', border: 'rgba(99, 102, 241, 0.3)', text: '#6366f1' },
    rose: { bg: 'rgba(244, 63, 94, 0.1)', border: 'rgba(244, 63, 94, 0.3)', text: '#f43f5e' },
    teal: { bg: 'rgba(20, 184, 166, 0.1)', border: 'rgba(20, 184, 166, 0.3)', text: '#14b8a6' },
    pink: { bg: 'rgba(236, 72, 153, 0.1)', border: 'rgba(236, 72, 153, 0.3)', text: '#ec4899' },
    orange: { bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.3)', text: '#f97316' },
    green: { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', text: '#22c55e' },
    red: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', text: '#ef4444' },
    sky: { bg: 'rgba(14, 165, 233, 0.1)', border: 'rgba(14, 165, 233, 0.3)', text: '#0ea5e9' },
    gray: { bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)', text: '#6b7280' },
    neutral: { bg: 'rgba(115, 115, 115, 0.1)', border: 'rgba(115, 115, 115, 0.3)', text: '#737373' },
  };
  return colors[color] || colors.slate;
};

const getCategoryIcon = (categoryId) => {
  const cat = EXPENSE_CATEGORIES.find(c => c.id === categoryId);
  return cat?.icon || Package;
};

const getSubcategoryName = (categoryId, subcategoryId) => {
  const cat = EXPENSE_CATEGORIES.find(c => c.id === categoryId);
  const sub = cat?.subcategories.find(s => s.id === subcategoryId);
  return sub?.name || subcategoryId?.replace(/_/g, ' ') || 'Unknown';
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function CompanyOperations() {
  const [activeTab, setActiveTab] = useState('expenses');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState('monthly');
  const [user, setUser] = useState(null);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          // API returns user directly (not wrapped in {user: ...})
          setUser(data.user || data);
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
      }
    };
    fetchUser();
  }, []);

  // Fetch data
  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ year: selectedYear });
      if (viewMode === 'monthly') {
        params.append('month', selectedMonth);
      }
      
      const response = await fetch(`${API}/api/company-expenses/summary?${params}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, viewMode]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const tabs = [
    { id: 'expenses', label: 'Expenses', icon: Receipt, color: 'emerald' },
    { id: 'scan', label: 'Scan', icon: Scan, color: 'blue' },
    { id: 'bank', label: 'Bank Feed', icon: Landmark, color: 'purple' },
    { id: 'mileage', label: 'Mileage', icon: Gauge, color: 'amber' },
    { id: 'rules', label: 'Rules', icon: BookOpen, color: 'orange' },
  ];

  return (
    <div 
      className="min-h-screen p-6"
      style={{ backgroundColor: '#020617', fontFamily: "'Inter', sans-serif" }}
      data-testid="company-operations-page"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-emerald-400" />
              <h1 className="text-2xl font-bold text-white">COMPANY OPERATIONS</h1>
            </div>
            <p className="text-slate-500 mt-1">AI-powered accounting, expense tracking & bank reconciliation</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div 
          className="flex gap-2 mb-6 p-2 rounded-lg"
          style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive 
                    ? `bg-${tab.color}-500/20 text-${tab.color}-400 border border-${tab.color}-500/40`
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                style={isActive ? {
                  backgroundColor: tab.color === 'emerald' ? 'rgba(16, 185, 129, 0.2)' :
                                   tab.color === 'blue' ? 'rgba(59, 130, 246, 0.2)' :
                                   tab.color === 'purple' ? 'rgba(139, 92, 246, 0.2)' :
                                   'rgba(245, 158, 11, 0.2)',
                  borderColor: tab.color === 'emerald' ? 'rgba(16, 185, 129, 0.4)' :
                               tab.color === 'blue' ? 'rgba(59, 130, 246, 0.4)' :
                               tab.color === 'purple' ? 'rgba(139, 92, 246, 0.4)' :
                               'rgba(245, 158, 11, 0.4)',
                  color: tab.color === 'emerald' ? '#10b981' :
                         tab.color === 'blue' ? '#3b82f6' :
                         tab.color === 'purple' ? '#8b5cf6' :
                         '#f59e0b'
                } : {}}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'expenses' && (
          <ExpensesTab 
            summary={summary}
            loading={loading}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            viewMode={viewMode}
            setViewMode={setViewMode}
            onRefresh={fetchSummary}
          />
        )}
        {activeTab === 'scan' && <ScanTab onExpenseCreated={fetchSummary} />}
        {activeTab === 'bank' && <BankFeedTab onRefresh={fetchSummary} />}
        {activeTab === 'mileage' && <MileageTab user={user} />}
        {activeTab === 'rules' && <RulesTab />}
      </div>

      {/* AI Copilot Button */}
      <AICopilotButton />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPENSES TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function ExpensesTab({ summary, loading, selectedYear, setSelectedYear, selectedMonth, setSelectedMonth, viewMode, setViewMode, onRefresh }) {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPODetail, setShowPODetail] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [budgetAlerts, setBudgetAlerts] = useState({ enabled: true, threshold75: true, threshold90: true, threshold100: true });
  const [showBudgetSettings, setShowBudgetSettings] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [newExpense, setNewExpense] = useState({
    category_id: '',
    subcategory_id: '',
    vendor: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    invoice_number: ''
  });

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/api/company-expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newExpense,
          amount: parseFloat(newExpense.amount)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Expense added! PO #${data.po_number}`);
        setShowAddModal(false);
        setNewExpense({ category_id: '', subcategory_id: '', vendor: '', amount: '', date: new Date().toISOString().split('T')[0], description: '', invoice_number: '' });
        onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add expense');
      }
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await fetch(`${API}/api/company-expenses?year=${selectedYear}`, { credentials: 'include' });
      const data = await response.json();
      const expenses = data.expenses || [];
      
      const csvContent = [
        ['Date', 'Category', 'Subcategory', 'Vendor', 'Amount', 'Description', 'Invoice #', 'PO Number'].join(','),
        ...expenses.map(e => [
          e.date,
          e.category_id,
          e.subcategory_id,
          `"${(e.vendor || '').replace(/"/g, '""')}"`,
          e.amount,
          `"${(e.description || '').replace(/"/g, '""')}"`,
          e.invoice_number || '',
          e.po_number || ''
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expenses_${selectedYear}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const budgetProgress = summary ? ((summary.ytd_total / summary.annual_budget) * 100) : 0;

  // Budget alerts
  const activeAlerts = [];
  if (budgetAlerts.enabled && summary) {
    if (budgetAlerts.threshold100 && budgetProgress >= 100 && !dismissedAlerts.includes('100')) {
      activeAlerts.push({ id: '100', type: 'critical', title: 'Budget Exceeded!', message: `You've spent ${budgetProgress.toFixed(0)}% of your annual budget` });
    } else if (budgetAlerts.threshold90 && budgetProgress >= 90 && !dismissedAlerts.includes('90')) {
      activeAlerts.push({ id: '90', type: 'warning', title: 'Budget Critical', message: `You've used ${budgetProgress.toFixed(0)}% of your annual budget` });
    } else if (budgetAlerts.threshold75 && budgetProgress >= 75 && !dismissedAlerts.includes('75')) {
      activeAlerts.push({ id: '75', type: 'info', title: 'Budget Alert', message: `You've used ${budgetProgress.toFixed(0)}% of your annual budget` });
    }
  }

  return (
    <div>
      {/* Budget Alerts */}
      {activeAlerts.map(alert => (
        <div 
          key={alert.id}
          className={`mb-4 p-4 rounded-lg flex items-center justify-between ${
            alert.type === 'critical' ? 'bg-red-500/20 border border-red-500/40' :
            alert.type === 'warning' ? 'bg-amber-500/20 border border-amber-500/40' :
            'bg-yellow-500/20 border border-yellow-500/40'
          }`}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className={`h-5 w-5 ${
              alert.type === 'critical' ? 'text-red-400' : alert.type === 'warning' ? 'text-amber-400' : 'text-yellow-400'
            }`} />
            <div>
              <p className={`font-bold ${alert.type === 'critical' ? 'text-red-400' : alert.type === 'warning' ? 'text-amber-400' : 'text-yellow-400'}`}>
                {alert.title}
              </p>
              <p className="text-slate-300 text-sm">{alert.message}</p>
            </div>
          </div>
          <button onClick={() => setDismissedAlerts(prev => [...prev, alert.id])} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
      ))}

      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {['monthly', 'yearly'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === mode ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400'
              }`}
            >
              {mode === 'monthly' ? 'Monthly' : 'This Year'}
            </button>
          ))}
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700"
          >
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          
          <button onClick={() => setShowBudgetSettings(!showBudgetSettings)} className={`p-2 rounded-lg ${budgetAlerts.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
            {budgetAlerts.enabled ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
          </button>
          
          <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white border border-slate-700">
            <Download className="h-4 w-4" /> Export
          </button>
          
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-white font-bold hover:bg-emerald-600" data-testid="add-expense-btn">
            <Plus className="h-5 w-5" /> Add Expense
          </button>
        </div>
      </div>

      {/* Month Selector */}
      {viewMode === 'monthly' && (
        <div className="flex gap-2 flex-wrap mb-6 p-4 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
          {MONTHS.map((month, idx) => (
            <button
              key={month}
              onClick={() => setSelectedMonth(idx + 1)}
              className={`px-3 py-2 rounded text-sm font-medium transition-all ${
                selectedMonth === idx + 1 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800/50 text-slate-400 hover:text-white'
              }`}
            >
              {month}
            </button>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">{viewMode === 'monthly' ? MONTHS[selectedMonth - 1] : 'Year'} Total</p>
          <p className="text-2xl font-bold text-white font-mono">${(summary?.period_total || 0).toLocaleString()}</p>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Year to Date</p>
          <p className="text-2xl font-bold text-white font-mono">${(summary?.ytd_total || 0).toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-1">of ${(summary?.annual_budget || 450000).toLocaleString()} budget</p>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Budget Remaining</p>
          <p className={`text-2xl font-bold font-mono ${(summary?.budget_remaining || 0) > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            ${(summary?.budget_remaining || 0).toLocaleString()}
          </p>
          <div className="mt-2 h-2 rounded-full bg-slate-700 overflow-hidden">
            <div className={`h-full rounded-full ${budgetProgress > 90 ? 'bg-red-500' : budgetProgress > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(budgetProgress, 100)}%` }} />
          </div>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Monthly Average</p>
          <p className="text-2xl font-bold text-white font-mono">${(summary?.monthly_average || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Main Content - Two Columns */}
      <div className="grid grid-cols-3 gap-6">
        {/* Expense Breakdown */}
        <div className="col-span-2 rounded-lg p-5" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-lg font-bold text-white mb-4">Expense Breakdown</h2>
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading...</div>
          ) : (
            <div className="space-y-2">
              {EXPENSE_CATEGORIES.map(category => {
                const categoryData = summary?.by_category?.find(c => c.category_id === category.id);
                const total = categoryData?.total || 0;
                const count = categoryData?.count || 0;
                const isExpanded = expandedCategories[category.id];
                const colorStyle = getCategoryColor(category.color);
                const Icon = category.icon;
                
                return (
                  <div key={category.id}>
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full px-4 py-3 rounded-lg flex items-center justify-between transition-all hover:opacity-90"
                      style={{ backgroundColor: colorStyle.bg, border: `1px solid ${colorStyle.border}` }}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        <Icon className="h-5 w-5" style={{ color: colorStyle.text }} />
                        <span className="text-white font-medium">{category.name}</span>
                        {count > 0 && <span className="text-xs text-slate-500">({count})</span>}
                      </div>
                      <span className="text-white font-bold font-mono">${total.toLocaleString()}</span>
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-8 mt-1 space-y-1">
                        {category.subcategories.map(sub => {
                          const subItems = categoryData?.items?.filter(item => item.subcategory_id === sub.id) || [];
                          const subTotal = subItems.reduce((sum, item) => sum + (item.amount || 0), 0);
                          
                          return (
                            <div key={sub.id}>
                              <div className="px-4 py-2 rounded flex items-center justify-between bg-slate-800/50">
                                <span className="text-slate-300 text-sm">{sub.name}</span>
                                <span className="text-slate-300 font-mono text-sm">${subTotal.toLocaleString()}</span>
                              </div>
                              {subItems.map((item, idx) => (
                                <div
                                  key={item.expense_id || idx}
                                  onClick={() => { setSelectedPO(item); setShowPODetail(true); }}
                                  className="ml-4 px-3 py-2 rounded flex items-center justify-between cursor-pointer hover:bg-emerald-500/10 transition-all"
                                  style={{ backgroundColor: 'rgba(15, 23, 42, 0.3)' }}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-500 text-xs">•</span>
                                    <span className="text-slate-400 text-sm">{item.vendor}</span>
                                    <span className="text-slate-600 text-xs">{item.date}</span>
                                  </div>
                                  <span className="text-emerald-400/70 font-mono text-sm">${(item.amount || 0).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Top Categories */}
          <div className="rounded-lg p-5" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Top Categories</h3>
            <div className="space-y-3">
              {(summary?.by_category || []).slice(0, 5).map((cat, idx) => {
                const categoryDef = EXPENSE_CATEGORIES.find(c => c.id === cat.category_id);
                const colorStyle = getCategoryColor(categoryDef?.color || 'slate');
                const Icon = categoryDef?.icon || Package;
                
                return (
                  <div key={cat.category_id} className="flex items-center gap-3">
                    <span className="text-slate-500 text-sm w-4">{idx + 1}</span>
                    <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: colorStyle.bg }}>
                      <Icon className="h-4 w-4" style={{ color: colorStyle.text }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{cat.name || cat.category_id}</p>
                      <p className="text-slate-500 text-xs">{cat.count} transactions</p>
                    </div>
                    <span className="text-white font-mono text-sm font-bold">${cat.total.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="rounded-lg p-5" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Recent Transactions</h3>
            <div className="space-y-3">
              {(summary?.recent_transactions || []).map((tx, idx) => {
                const Icon = getCategoryIcon(tx.category_id);
                const categoryDef = EXPENSE_CATEGORIES.find(c => c.id === tx.category_id);
                const colorStyle = getCategoryColor(categoryDef?.color || 'slate');
                
                return (
                  <div 
                    key={tx.expense_id || idx} 
                    className="flex items-center gap-3 cursor-pointer hover:bg-slate-800/50 rounded-lg p-2 -mx-2"
                    onClick={() => { setSelectedPO(tx); setShowPODetail(true); }}
                  >
                    <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colorStyle.bg }}>
                      <Icon className="h-5 w-5" style={{ color: colorStyle.text }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{tx.vendor}</p>
                      <p className="text-slate-500 text-xs">{getSubcategoryName(tx.category_id, tx.subcategory_id)} • {tx.date}</p>
                    </div>
                    <span className="text-white font-mono text-sm font-bold">${(tx.amount || 0).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* P&L Link */}
          <a href="/reports?tab=pnl" className="block p-4 rounded-lg text-center transition-all hover:opacity-90" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(59, 130, 246, 0.2))', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <p className="text-emerald-400 font-bold">View in P&L Report →</p>
            <p className="text-slate-500 text-xs mt-1">See how overhead affects profitability</p>
          </a>
        </div>
      </div>

      {/* Add Expense Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }} onClick={() => setShowAddModal(false)}>
          <div className="w-full max-w-lg rounded-lg p-6" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(16, 185, 129, 0.3)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Add Expense</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">Category *</label>
                <select value={newExpense.category_id} onChange={e => setNewExpense(prev => ({ ...prev, category_id: e.target.value, subcategory_id: '' }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required>
                  <option value="">Select category...</option>
                  {EXPENSE_CATEGORIES.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              
              {newExpense.category_id && (
                <div>
                  <label className="block text-xs text-slate-500 uppercase mb-1">Subcategory *</label>
                  <select value={newExpense.subcategory_id} onChange={e => setNewExpense(prev => ({ ...prev, subcategory_id: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required>
                    <option value="">Select subcategory...</option>
                    {EXPENSE_CATEGORIES.find(c => c.id === newExpense.category_id)?.subcategories.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">Vendor *</label>
                <input type="text" value={newExpense.vendor} onChange={e => setNewExpense(prev => ({ ...prev, vendor: e.target.value }))} placeholder="e.g., Shell Gas Station" className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 uppercase mb-1">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <input type="number" step="0.01" value={newExpense.amount} onChange={e => setNewExpense(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" className="w-full pl-7 pr-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700 font-mono" required />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 uppercase mb-1">Date *</label>
                  <input type="date" value={newExpense.date} onChange={e => setNewExpense(prev => ({ ...prev, date: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">Description</label>
                <input type="text" value={newExpense.description} onChange={e => setNewExpense(prev => ({ ...prev, description: e.target.value }))} placeholder="Optional description" className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 rounded-lg bg-slate-700 text-white">Cancel</button>
                <button type="submit" className="px-6 py-2 rounded-lg bg-emerald-500 text-white font-bold hover:bg-emerald-600">Add Expense</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PO Detail Modal */}
      {showPODetail && selectedPO && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }} onClick={() => { setShowPODetail(false); setSelectedPO(null); }}>
          <div className="rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(16, 185, 129, 0.3)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <div>
                <h2 className="text-xl font-bold text-emerald-400 font-mono">{selectedPO.po_number || 'PURCHASE ORDER'}</h2>
                <p className="text-slate-400 text-sm mt-1">{selectedPO.vendor}</p>
              </div>
              <button onClick={() => { setShowPODetail(false); setSelectedPO(null); }} className="p-2 rounded-lg text-slate-400 hover:text-emerald-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ backgroundColor: '#020617' }}>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                  <p className="text-slate-500 text-xs uppercase mb-1">Amount</p>
                  <p className="text-2xl font-bold text-emerald-400 font-mono">${(selectedPO.amount || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                  <p className="text-slate-500 text-xs uppercase mb-1">Date</p>
                  <p className="text-lg font-semibold text-white font-mono">{selectedPO.date || 'N/A'}</p>
                </div>
              </div>
              
              {selectedPO.account_code && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.4)' }}>
                  <p className="text-slate-500 text-xs uppercase mb-2">Account (COA)</p>
                  <p className="text-emerald-400 font-mono font-bold text-xl">{selectedPO.account_code}</p>
                  <p className="text-slate-400 text-sm mt-1">{selectedPO.account_name}</p>
                </div>
              )}
              
              {selectedPO.description && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(100, 116, 139, 0.2)' }}>
                  <p className="text-slate-500 text-xs uppercase mb-2">Description</p>
                  <p className="text-white">{selectedPO.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SCAN TAB COMPONENT - AI Receipt Scanning
// ═══════════════════════════════════════════════════════════════════════════

function ScanTab({ onExpenseCreated }) {
  const [pendingDocs, setPendingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({});

  const fetchPendingDocs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/document-ai/pending`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPendingDocs(data.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending docs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingDocs();
  }, [fetchPendingDocs]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/api/document-ai/scan`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Document scanned! Review the extracted data.');
        fetchPendingDocs();
        
        // Open review modal with extracted data
        if (data.extracted_data) {
          setSelectedDoc({ document_id: data.document_id, ...data.extracted_data });
          setReviewData({
            vendor: data.extracted_data.vendor || '',
            amount: data.extracted_data.amount || '',
            date: data.extracted_data.date || new Date().toISOString().split('T')[0],
            description: data.extracted_data.description || '',
            category_id: data.extracted_data.suggested_category || '',
            subcategory_id: data.extracted_data.suggested_subcategory || '',
            invoice_number: data.extracted_data.invoice_number || ''
          });
          setShowReviewModal(true);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Scan failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleApprove = async () => {
    if (!selectedDoc?.document_id) return;

    try {
      const response = await fetch(`${API}/api/document-ai/documents/${selectedDoc.document_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(reviewData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Expense created! PO #${data.po_number}`);
        setShowReviewModal(false);
        setSelectedDoc(null);
        fetchPendingDocs();
        onExpenseCreated?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Approval failed');
    }
  };

  const handleDelete = async (docId) => {
    try {
      await fetch(`${API}/api/document-ai/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Document deleted');
      fetchPendingDocs();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div 
        className="p-8 rounded-lg text-center border-2 border-dashed transition-all hover:border-blue-500/50"
        style={{ backgroundColor: '#0f172a', borderColor: 'rgba(59, 130, 246, 0.3)' }}
      >
        <input
          type="file"
          id="receipt-upload"
          className="hidden"
          accept="image/*,.pdf"
          onChange={handleFileUpload}
          disabled={uploading}
        />
        <label htmlFor="receipt-upload" className="cursor-pointer">
          <div className="flex flex-col items-center gap-4">
            {uploading ? (
              <>
                <Loader2 className="h-12 w-12 text-blue-400 animate-spin" />
                <p className="text-blue-400 font-medium">Scanning with AI...</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Scan className="h-8 w-8 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">Scan Receipt or Invoice</p>
                  <p className="text-slate-400 text-sm mt-1">Drop a file or click to upload (JPG, PNG, PDF)</p>
                </div>
                <p className="text-blue-400 text-sm">
                  <Sparkles className="h-4 w-4 inline mr-1" />
                  AI will automatically extract vendor, amount, and suggest a category
                </p>
              </>
            )}
          </div>
        </label>
      </div>

      {/* Pending Documents */}
      <div className="rounded-lg p-5" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-amber-400" />
          Pending Review ({pendingDocs.length})
        </h3>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : pendingDocs.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-slate-400">No documents pending review</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingDocs.map(doc => (
              <div 
                key={doc.document_id}
                className="p-4 rounded-lg flex items-center justify-between"
                style={{ backgroundColor: '#020617', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded bg-slate-800 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{doc.filename || 'Scanned Document'}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {doc.extracted_data?.vendor && (
                        <span className="text-slate-400 text-sm">{doc.extracted_data.vendor}</span>
                      )}
                      {doc.extracted_data?.amount && (
                        <span className="text-emerald-400 font-mono text-sm">${doc.extracted_data.amount}</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        doc.status === 'pending_review' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {doc.status === 'pending_review' ? 'Ready for Review' : 'Manual Entry'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedDoc({ document_id: doc.document_id, ...(doc.extracted_data || {}) });
                      setReviewData({
                        vendor: doc.extracted_data?.vendor || '',
                        amount: doc.extracted_data?.amount || '',
                        date: doc.extracted_data?.date || new Date().toISOString().split('T')[0],
                        description: doc.extracted_data?.description || '',
                        category_id: doc.extracted_data?.suggested_category || '',
                        subcategory_id: doc.extracted_data?.suggested_subcategory || '',
                        invoice_number: doc.extracted_data?.invoice_number || ''
                      });
                      setShowReviewModal(true);
                    }}
                    className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 font-medium hover:bg-emerald-500/30"
                  >
                    Review
                  </button>
                  <button
                    onClick={() => handleDelete(doc.document_id)}
                    className="p-2 rounded-lg text-red-400 hover:bg-red-500/20"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }} onClick={() => setShowReviewModal(false)}>
          <div className="w-full max-w-lg rounded-lg p-6" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(59, 130, 246, 0.3)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Sparkles className="h-6 w-6 text-blue-400" />
                <h2 className="text-xl font-bold text-white">Review AI Extraction</h2>
              </div>
              <button onClick={() => setShowReviewModal(false)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedDoc.confidence && (
              <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-blue-400 text-sm">
                  AI Confidence: <span className="font-bold">{Math.round(selectedDoc.confidence * 100)}%</span>
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">Vendor *</label>
                <input type="text" value={reviewData.vendor} onChange={e => setReviewData(prev => ({ ...prev, vendor: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 uppercase mb-1">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <input type="number" step="0.01" value={reviewData.amount} onChange={e => setReviewData(prev => ({ ...prev, amount: e.target.value }))} className="w-full pl-7 pr-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700 font-mono" required />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 uppercase mb-1">Date *</label>
                  <input type="date" value={reviewData.date} onChange={e => setReviewData(prev => ({ ...prev, date: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required />
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">Category *</label>
                <select value={reviewData.category_id} onChange={e => setReviewData(prev => ({ ...prev, category_id: e.target.value, subcategory_id: '' }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required>
                  <option value="">Select category...</option>
                  {EXPENSE_CATEGORIES.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>

              {reviewData.category_id && (
                <div>
                  <label className="block text-xs text-slate-500 uppercase mb-1">Subcategory *</label>
                  <select value={reviewData.subcategory_id} onChange={e => setReviewData(prev => ({ ...prev, subcategory_id: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required>
                    <option value="">Select subcategory...</option>
                    {EXPENSE_CATEGORIES.find(c => c.id === reviewData.category_id)?.subcategories.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">Description</label>
                <input type="text" value={reviewData.description} onChange={e => setReviewData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowReviewModal(false)} className="px-4 py-2 rounded-lg bg-slate-700 text-white">Cancel</button>
              <button onClick={handleApprove} className="px-6 py-2 rounded-lg bg-emerald-500 text-white font-bold hover:bg-emerald-600 flex items-center gap-2">
                <Check className="h-4 w-4" /> Approve & Create Expense
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BANK FEED TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function BankFeedTab({ onRefresh }) {
  const [plaidStatus, setPlaidStatus] = useState(null);
  const [connections, setConnections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionCounts, setTransactionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('unmatched');

  const fetchData = useCallback(async () => {
    try {
      // Get Plaid status
      const statusRes = await fetch(`${API}/api/plaid/status`, { credentials: 'include' });
      if (statusRes.ok) {
        const status = await statusRes.json();
        setPlaidStatus(status);
      }

      // Get connections
      const connRes = await fetch(`${API}/api/plaid/connections`, { credentials: 'include' });
      if (connRes.ok) {
        const data = await connRes.json();
        setConnections(data.connections || []);
      }

      // Get transactions
      const txnRes = await fetch(`${API}/api/plaid/transactions?status=${filter}`, { credentials: 'include' });
      if (txnRes.ok) {
        const data = await txnRes.json();
        setTransactions(data.transactions || []);
        setTransactionCounts(data.counts || {});
      }
    } catch (error) {
      console.error('Failed to fetch bank data:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async (connectionId) => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/api/plaid/connections/${connectionId}/sync`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {plaidStatus && !plaidStatus.configured && (
        <div className="p-4 rounded-lg bg-amber-500/20 border border-amber-500/40">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
            <div>
              <p className="text-amber-400 font-bold">Bank Connection Not Configured</p>
              <p className="text-slate-300 text-sm">Plaid API keys are required to connect bank accounts.</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Unmatched</p>
          <p className="text-2xl font-bold text-amber-400 font-mono">{transactionCounts.unmatched || 0}</p>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Matched</p>
          <p className="text-2xl font-bold text-blue-400 font-mono">{transactionCounts.matched || 0}</p>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Reconciled</p>
          <p className="text-2xl font-bold text-emerald-400 font-mono">{transactionCounts.reconciled || 0}</p>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Connected Banks</p>
          <p className="text-2xl font-bold text-purple-400 font-mono">{connections.length}</p>
        </div>
      </div>

      {/* Connected Banks */}
      <div className="rounded-lg p-5" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Landmark className="h-5 w-5 text-purple-400" />
            Connected Banks
          </h3>
          {plaidStatus?.configured && (
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 font-medium hover:bg-purple-500/30">
              <Plus className="h-4 w-4" /> Connect Bank
            </button>
          )}
        </div>

        {connections.length === 0 ? (
          <div className="text-center py-8">
            <Landmark className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No banks connected yet</p>
            {plaidStatus?.configured && (
              <p className="text-slate-500 text-sm mt-1">Click &quot;Connect Bank&quot; to link your first account</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {connections.map(conn => (
              <div key={conn.connection_id} className="p-4 rounded-lg flex items-center justify-between" style={{ backgroundColor: '#020617', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Landmark className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{conn.institution_name}</p>
                    <p className="text-slate-500 text-sm">{conn.accounts?.length || 0} accounts • Last synced: {conn.last_synced_at ? new Date(conn.last_synced_at).toLocaleDateString() : 'Never'}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSync(conn.connection_id)}
                  disabled={syncing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 font-medium hover:bg-emerald-500/30 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                  Sync
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className="rounded-lg p-5" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Bank Transactions</h3>
          <div className="flex gap-2">
            {['unmatched', 'matched', 'reconciled', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                  filter === f ? 'bg-purple-500/20 text-purple-400' : 'text-slate-400 hover:text-white'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8">
            <Banknote className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No transactions to display</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map(txn => (
              <div key={txn.transaction_id} className="p-4 rounded-lg flex items-center justify-between" style={{ backgroundColor: '#020617', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded flex items-center justify-center ${
                    txn.amount > 0 ? 'bg-red-500/20' : 'bg-emerald-500/20'
                  }`}>
                    {txn.amount > 0 ? <TrendingUp className="h-5 w-5 text-red-400" /> : <TrendingDown className="h-5 w-5 text-emerald-400" />}
                  </div>
                  <div>
                    <p className="text-white font-medium">{txn.merchant_name || txn.description}</p>
                    <p className="text-slate-500 text-sm">{txn.date} • {txn.category?.join(', ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`font-mono font-bold ${txn.amount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {txn.amount > 0 ? '-' : ''}${Math.abs(txn.amount).toLocaleString()}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    txn.is_reconciled ? 'bg-emerald-500/20 text-emerald-400' :
                    txn.match_status === 'matched' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {txn.is_reconciled ? 'Reconciled' : txn.match_status === 'matched' ? 'Matched' : 'Unmatched'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// RULES TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function RulesTab() {
  const [rules, setRules] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRule, setNewRule] = useState({
    rule_type: 'vendor_match',
    vendor_pattern: '',
    assign_category_id: '',
    assign_subcategory_id: ''
  });

  const fetchRules = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/rules?include_inactive=true`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRules(data.rules || []);
        setCounts(data.counts || {});
      }
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/api/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newRule)
      });

      if (response.ok) {
        toast.success('Rule created');
        setShowAddModal(false);
        setNewRule({ rule_type: 'vendor_match', vendor_pattern: '', assign_category_id: '', assign_subcategory_id: '' });
        fetchRules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create rule');
      }
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const handleToggle = async (ruleId) => {
    try {
      const response = await fetch(`${API}/api/rules/${ruleId}/toggle`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchRules();
      }
    } catch (error) {
      toast.error('Failed to toggle rule');
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await fetch(`${API}/api/rules/${ruleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Total Rules</p>
          <p className="text-2xl font-bold text-amber-400 font-mono">{counts.total || 0}</p>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Active</p>
          <p className="text-2xl font-bold text-emerald-400 font-mono">{counts.active || 0}</p>
        </div>
        <div className="p-5 rounded-lg" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
          <p className="text-xs text-slate-500 uppercase mb-1">Auto-Learned</p>
          <p className="text-2xl font-bold text-blue-400 font-mono">{counts.auto_learned || 0}</p>
        </div>
      </div>

      {/* Rules List */}
      <div className="rounded-lg p-5" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-amber-400" />
            Categorization Rules
          </h3>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 font-medium hover:bg-amber-500/30">
            <Plus className="h-4 w-4" /> Add Rule
          </button>
        </div>

        <p className="text-slate-400 text-sm mb-4">
          Rules automatically categorize transactions based on patterns. AI learns new rules from your manual categorizations.
        </p>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : rules.length === 0 ? (
          <div className="text-center py-8">
            <BookOpen className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No rules yet</p>
            <p className="text-slate-500 text-sm mt-1">Rules will be auto-created as you categorize expenses</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map(rule => {
              const category = EXPENSE_CATEGORIES.find(c => c.id === rule.assign_category_id);
              
              return (
                <div 
                  key={rule.rule_id}
                  className={`p-4 rounded-lg flex items-center justify-between ${!rule.is_active ? 'opacity-50' : ''}`}
                  style={{ backgroundColor: '#020617', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded flex items-center justify-center ${rule.auto_learned ? 'bg-blue-500/20' : 'bg-amber-500/20'}`}>
                      {rule.auto_learned ? <Sparkles className="h-5 w-5 text-blue-400" /> : <BookOpen className="h-5 w-5 text-amber-400" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{rule.rule_name || rule.vendor_pattern}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-slate-500 text-sm">
                          When vendor contains &quot;{rule.vendor_pattern}&quot;
                        </span>
                        <ArrowRight className="h-3 w-3 text-slate-600" />
                        <span className="text-emerald-400 text-sm">{category?.name || rule.assign_category_id}</span>
                      </div>
                      <p className="text-slate-600 text-xs mt-1">{rule.match_count || 0} matches</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(rule.rule_id)}
                      className={`px-3 py-1.5 rounded text-sm font-medium ${
                        rule.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      {rule.is_active ? 'Active' : 'Disabled'}
                    </button>
                    <button onClick={() => handleDelete(rule.rule_id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/20">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Rule Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }} onClick={() => setShowAddModal(false)}>
          <div className="w-full max-w-lg rounded-lg p-6" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(245, 158, 11, 0.3)' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Add Categorization Rule</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateRule} className="space-y-4">
              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">When vendor contains *</label>
                <input type="text" value={newRule.vendor_pattern} onChange={e => setNewRule(prev => ({ ...prev, vendor_pattern: e.target.value }))} placeholder="e.g., Shell, Home Depot" className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required />
              </div>

              <div>
                <label className="block text-xs text-slate-500 uppercase mb-1">Assign to Category *</label>
                <select value={newRule.assign_category_id} onChange={e => setNewRule(prev => ({ ...prev, assign_category_id: e.target.value, assign_subcategory_id: '' }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required>
                  <option value="">Select category...</option>
                  {EXPENSE_CATEGORIES.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>

              {newRule.assign_category_id && (
                <div>
                  <label className="block text-xs text-slate-500 uppercase mb-1">Subcategory *</label>
                  <select value={newRule.assign_subcategory_id} onChange={e => setNewRule(prev => ({ ...prev, assign_subcategory_id: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700" required>
                    <option value="">Select subcategory...</option>
                    {EXPENSE_CATEGORIES.find(c => c.id === newRule.assign_category_id)?.subcategories.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 rounded-lg bg-slate-700 text-white">Cancel</button>
                <button type="submit" className="px-6 py-2 rounded-lg bg-amber-500 text-white font-bold hover:bg-amber-600">Create Rule</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AI COPILOT BUTTON COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function AICopilotButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    const userQuery = query;
    setQuery('');

    try {
      const res = await fetch(`${API}/api/document-ai/copilot/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: userQuery })
      });

      if (res.ok) {
        const data = await res.json();
        setHistory(prev => [...prev, { query: userQuery, response: data.response }]);
        setResponse(data.response);
      } else {
        toast.error('Query failed');
      }
    } catch (error) {
      toast.error('AI query failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        data-testid="ai-copilot-btn"
      >
        <Bot className="h-6 w-6" />
        <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-emerald-400 animate-pulse" />
      </button>

      {/* Chat Modal */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 max-h-[500px] rounded-xl shadow-2xl flex flex-col overflow-hidden" style={{ backgroundColor: '#0f172a', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
          {/* Header */}
          <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(139, 92, 246, 0.2)' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">AI Financial Assistant</p>
                <p className="text-slate-500 text-xs">Ask about your expenses</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Chat History */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-80" style={{ backgroundColor: '#020617' }}>
            {history.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm">Try asking:</p>
                <div className="space-y-2 mt-2">
                  {[
                    "What are my top expenses?",
                    "How much did I spend on fuel?",
                    "Am I over budget?"
                  ].map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setQuery(suggestion)}
                      className="block w-full text-left px-3 py-2 rounded-lg bg-slate-800/50 text-slate-300 text-sm hover:bg-slate-800"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              history.map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-end mb-2">
                    <div className="bg-purple-500/20 px-3 py-2 rounded-lg max-w-[80%]">
                      <p className="text-purple-300 text-sm">{item.query}</p>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-slate-800 px-3 py-2 rounded-lg max-w-[80%]">
                      <p className="text-white text-sm whitespace-pre-wrap">{item.response}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex items-center gap-2 text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Thinking...</span>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex gap-2">
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Ask about your finances..."
                className="flex-1 px-3 py-2 rounded-lg bg-slate-800 text-white border border-slate-700 text-sm"
                disabled={loading}
              />
              <button type="submit" disabled={loading || !query.trim()} className="px-4 py-2 rounded-lg bg-purple-500 text-white disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
